import React, { useState, useEffect } from "react";
import { Modal, Tabs, Table, Avatar, Tag, Button } from "antd";
import { Link } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { ShowToast, Severty } from "../../helper/toast";

const { TabPane } = Tabs;

function ViewTournamentLeaderBoard({ open, onCancel, tournamentId }) {
    const { request } = useRequest();
    const [leaderboardList, setLeaderboardList] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [leaderboardPagination, setLeaderboardPagination] = useState({ current: 1, pageSize: 10 });
    const [skills, setSkills] = useState([]);
    const [selectedSkill, setSelectedSkill] = useState("");

    // Fetch skill tags for dropdown/tab filter
    const fetchSkills = () => {
        request({
            url: apiPath.listSkillTags,
            method: "GET",
            onSuccess: ({ data }) => {
                let skillsArray = [];
                if (Array.isArray(data)) skillsArray = data;
                else if (Array.isArray(data?.data)) skillsArray = data.data;
                else if (Array.isArray(data?.data?.docs)) skillsArray = data.data.docs;
                else if (Array.isArray(data?.docs)) skillsArray = data.docs;

                const allowedSkills = ["batting", "fielding", "bowling"];
                const filteredSkills = skillsArray.filter(skill =>
                    skill?.name && allowedSkills.includes(skill.name.toLowerCase())
                );

                const sortOrder = { batting: 1, bowling: 2, fielding: 3 };
                filteredSkills.sort((a, b) => sortOrder[a.name.toLowerCase()] - sortOrder[b.name.toLowerCase()]);

                setSkills(filteredSkills);
            },
            onError: () => { },
        });
    };

    const fetchLeaderboardData = (paginationParams, skillId) => {
        if (!tournamentId) return;
        setLeaderboardLoading(true);
        const queryParams = new URLSearchParams();

        queryParams.append("page", paginationParams.current);
        queryParams.append("limit", paginationParams.pageSize);
        if (skillId) {
            queryParams.append("skillTagId", skillId);
        } else {
            queryParams.append("skillTagId", "");
        }

        request({
            url: `${apiPath.tournamentLeaderboard}/${tournamentId}?${queryParams.toString()}`,
            method: "GET",
            onSuccess: ({ data }) => {
                setLeaderboardLoading(false);
                setLeaderboardList(Array.isArray(data?.data) ? data.data : []);
                setLeaderboardPagination({
                    current: data?.pagination?.page || paginationParams.current,
                    total: data?.pagination?.totalUsers || 0,
                    pageSize: Number(data?.pagination?.limit) || 10,
                });
            },
            onError: (error) => {
                setLeaderboardLoading(false);
                ShowToast(error?.response?.data?.message || "Failed to fetch leaderboard data", Severty.ERROR);
            },
        });
    };

    useEffect(() => {
        if (open) {
            fetchSkills();
        }
    }, [open]);

    useEffect(() => {
        if (open && tournamentId) {
            fetchLeaderboardData({ current: 1, pageSize: leaderboardPagination.pageSize }, selectedSkill);
        }
    }, [open, tournamentId, selectedSkill]);

    const handleLeaderboardChange = (newPagination) => {
        fetchLeaderboardData(newPagination, selectedSkill);
    };

    const renderPodium = () => {
        if (leaderboardPagination.current !== 1 || leaderboardList.length === 0) return null;

        const first = leaderboardList[0];
        const second = leaderboardList.length > 1 ? leaderboardList[1] : null;
        const third = leaderboardList.length > 2 ? leaderboardList[2] : null;

        return (
            <div className="podium-container" style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                gap: '20px',
                margin: '12px 0 28px 0',
                flexWrap: 'wrap'
            }}>
                {/* 2nd Place */}
                {second && (
                    <div className="podium-card second" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        borderRadius: '12px',
                        padding: '16px',
                        width: '150px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                        position: 'relative',
                        order: 1
                    }}>
                        <span style={{ fontSize: '24px', position: 'absolute', top: '-15px' }}>🥈</span>
                        <Avatar src={second.avatar} size={64} style={{ border: '3px solid #cbd5e1', backgroundColor: '#e2700d' }}>
                            {second.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <div style={{ fontWeight: 600, marginTop: '10px', textAlign: 'center', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '130px' }}>
                            {second.name || "-"}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '130px', textAlign: 'center' }}>
                            @{second.username || "-"}
                        </div>
                        <div style={{ fontWeight: 'bold', color: '#f97316', fontSize: '15px', marginTop: '8px' }}>
                            {second.TotalTPSPoints ?? second.totalTPS ?? 0} TPS
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>Rank 2</div>
                    </div>
                )}

                {/* 1st Place */}
                {first && (
                    <div className="podium-card first" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        background: '#fffbeb',
                        border: '2px solid #fcd34d',
                        borderRadius: '16px',
                        padding: '24px 16px',
                        width: '170px',
                        boxShadow: '0 10px 15px -3px rgba(251, 191, 36, 0.2)',
                        position: 'relative',
                        order: 2,
                        transform: 'scale(1.05)',
                        zIndex: 2
                    }}>
                        <span style={{ fontSize: '32px', position: 'absolute', top: '-20px' }}>👑</span>
                        <Avatar src={first.avatar} size={80} style={{ border: '4px solid #fcd34d', backgroundColor: '#e2700d' }}>
                            {first.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <div style={{ fontWeight: 700, marginTop: '12px', textAlign: 'center', color: '#78350f', fontSize: '16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '150px' }}>
                            {first.name || "-"}
                        </div>
                        <div style={{ fontSize: '12px', color: '#b45309', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '150px', textAlign: 'center' }}>
                            @{first.username || "-"}
                        </div>
                        <div style={{ fontWeight: 'extrabold', color: '#f59e0b', fontSize: '18px', marginTop: '8px' }}>
                            {first.TotalTPSPoints ?? first.totalTPS ?? 0} TPS
                        </div>
                        <div style={{ fontSize: '11px', color: '#d97706', fontWeight: 600 }}>Rank 1</div>
                    </div>
                )}

                {/* 3rd Place */}
                {third && (
                    <div className="podium-card third" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        background: '#fdf8f6',
                        border: '1px solid #fdba74',
                        borderRadius: '12px',
                        padding: '16px',
                        width: '150px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                        position: 'relative',
                        order: 3
                    }}>
                        <span style={{ fontSize: '24px', position: 'absolute', top: '-15px' }}>🥉</span>
                        <Avatar src={third.avatar} size={64} style={{ border: '3px solid #fdba74', backgroundColor: '#e2700d' }}>
                            {third.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <div style={{ fontWeight: 600, marginTop: '10px', textAlign: 'center', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '130px' }}>
                            {third.name || "-"}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '130px', textAlign: 'center' }}>
                            @{third.username || "-"}
                        </div>
                        <div style={{ fontWeight: 'bold', color: '#ea580c', fontSize: '15px', marginTop: '8px' }}>
                            {third.TotalTPSPoints ?? third.totalTPS ?? 0} TPS
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>Rank 3</div>
                    </div>
                )}
            </div>
        );
    };

    const leaderboardColumns = [
        {
            title: "Rank",
            key: "rank",
            width: "5%",
            render: (_, record) => {
                const rank = record?.rank;
                if (rank === 1) return <strong style={{ color: '#FFD700', fontSize: '18px' }}>🥇 1</strong>;
                if (rank === 2) return <strong style={{ color: '#C0C0C0', fontSize: '18px' }}>🥈 2</strong>;
                if (rank === 3) return <strong style={{ color: '#CD7F32', fontSize: '18px' }}>🥉 3</strong>;
                return <strong>#{rank || "-"}</strong>;
            },
        },
        {
            title: "User Info",
            key: "user",
            width: "25%",
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Avatar src={record?.avatar} size="large">
                        {record?.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: "14px" }}>{record?.name || "-"}</div>
                        <div style={{ fontSize: "12px", color: "gray" }}>@{record?.username || "-"}</div>
                    </div>
                </div>
            ),
        },
        {
            title: "Role & Badges",
            key: "role",
            width: "20%",
            render: (_, record) => (
                <div>
                    {record?.cricketRole?.name && (
                        <Tag color="blue" style={{ marginBottom: '4px' }}>
                            {record.cricketRole.name}
                        </Tag>
                    )}
                    {record?.badges?.map((badge) => (
                        <Tag color="gold" key={badge._id} style={{ marginBottom: '4px', marginLeft: '4px' }}>
                            {badge.name}
                        </Tag>
                    ))}
                    {!record?.cricketRole?.name && (!record?.badges || record.badges.length === 0) && "-"}
                </div>
            ),
        },
        {
            title: "Posts",
            key: "posts",
            width: "8%",
            render: (_, record) => <span>{record?.totalPosts ?? 0}</span>,
        },
        {
            title: "Likes",
            key: "likes",
            width: "8%",
            render: (_, record) => <span>{record?.totalLikes ?? 0}</span>,
        },
        {
            title: "Engagement",
            key: "engagement",
            width: "12%",
            render: (_, record) => <span>{record?.totalEngagement ?? 0}</span>,
        },
        {
            title: "Leaderboard Points",
            key: "points",
            width: "15%",
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 'bold', fontSize: "15px", color: "#1890ff" }}>
                        {record?.TotalTPSPoints ?? record?.totalTPS ?? 0} TPS
                    </div>
                </div>
            ),
        },
        {
            title: "Action",
            key: "action",
            width: "10%",
            render: (_, record) => (
                <Link to={`/admin/user-manager/view/${record?.userId || record?.id}`} onClick={onCancel}>
                    <Button type="primary" size="small">
                        View
                    </Button>
                </Link>
            ),
        },
    ];

    return (
        <Modal
            title="Tournament Leaderboard"
            visible={open}
            onCancel={onCancel}
            footer={[
                <Button key="close" onClick={onCancel}>
                    Close
                </Button>
            ]}
            width={900}
            destroyOnClose
            centered
            bodyStyle={{ maxHeight: '75vh', overflowY: 'auto', padding: '24px' }}
        >
            {renderPodium()}

            <div className="table-responsive customPagination customPaginationmain-newwwww">
                <Tabs
                    activeKey={selectedSkill}
                    onChange={(key) => {
                        setSelectedSkill(key);
                        setLeaderboardPagination({ ...leaderboardPagination, current: 1 });
                    }}
                    type="card"
                >
                    <TabPane tab="All Skills" key="">
                        <Table
                            loading={leaderboardLoading}
                            columns={leaderboardColumns}
                            dataSource={leaderboardList}
                            rowKey={(record) => record?.userId || record?._id || record?.id || Math.random()}
                            pagination={{
                                current: leaderboardPagination.current,
                                defaultPageSize: 10,
                                responsive: true,
                                total: leaderboardPagination.total,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                pageSizeOptions: ["10", "20", "30", "50"],
                            }}
                            onChange={handleLeaderboardChange}
                            className="ant-border-space"
                        />
                    </TabPane>
                    {(skills || []).map((skill) => (
                        <TabPane
                            tab={skill.name.charAt(0).toUpperCase() + skill.name.slice(1).toLowerCase()}
                            key={skill._id || skill.id}
                        >
                            <Table
                                loading={leaderboardLoading}
                                columns={leaderboardColumns}
                                dataSource={leaderboardList}
                                rowKey={(record) => record?.userId || record?._id || record?.id || Math.random()}
                                pagination={{
                                    current: leaderboardPagination.current,
                                    defaultPageSize: 10,
                                    responsive: true,
                                    total: leaderboardPagination.total,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    pageSizeOptions: ["10", "20", "30", "50"],
                                }}
                                onChange={handleLeaderboardChange}
                                className="ant-border-space"
                            />
                        </TabPane>
                    ))}
                </Tabs>
            </div>
        </Modal>
    );
}

export default ViewTournamentLeaderBoard;
